/**
 * DealShareModal — Share a manual flight deal with a customer.
 *
 * Workflow: Edit customer-facing offer → live preview → generate PNG → WhatsApp
 *
 * The original deal is NEVER modified. All edits are stored in `customerOffer`
 * which is initialised from the deal on open but kept completely separate.
 *
 * Image generation uses SVG <foreignObject> to render the EXACT same HTML
 * template that TicketGenerator.js uses for real e-tickets — so the output
 * is pixel-identical to the Flight Tickets page branding.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { getAirportName } from "@/utils/airports";
import { Download, MessageCircle, Image as ImageIcon, Plus, Trash2 } from "lucide-react";

// ─── Data helpers ──────────────────────────────────────────────────────────────

function getDealSegments(deal) {
  if (deal.segments && deal.segments.length > 0) return deal.segments;
  return [{
    departure_airport: deal.departure_airport || "",
    arrival_airport:   deal.arrival_airport   || "",
    departure_time:    deal.departure_time    || "",
    arrival_time:      deal.arrival_time      || "",
    flight_number:     deal.flight_number     || "",
    operated_by:       "",
  }];
}

function isoToTimeStr(iso) {
  if (!iso) return "";
  if (/^\d{2}:\d{2}/.test(iso)) return iso.slice(0, 5);
  try { return new Date(iso).toTimeString().slice(0, 5); } catch { return ""; }
}

function isoToDateStr(iso) {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  try { return new Date(iso).toISOString().split("T")[0]; } catch { return ""; }
}

// Format YYYY-MM-DD → "12 Jun 2025" (matches TicketGenerator display)
function displayDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T12:00:00Z").toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}

// ─── Initialize customer offer from deal (deep copy — original never touched) ─

function initFromDeal(deal) {
  if (!deal) return null;
  const segs = getDealSegments(deal);
  const raw  = (deal.baggage_included || "").toLowerCase();
  const kgMatch = raw.match(/(\d+)\s*kg/);
  return {
    airline:     deal.airline     || "",
    cabin_class: deal.cabin_class || "Economy",
    segments: segs.map(s => ({
      departure_airport: s.departure_airport || "",
      arrival_airport:   s.arrival_airport   || "",
      departure_date:    isoToDateStr(s.departure_time),
      departure_time:    isoToTimeStr(s.departure_time),
      arrival_date:      isoToDateStr(s.arrival_time),
      arrival_time:      isoToTimeStr(s.arrival_time),
      flight_number:     s.flight_number || "",
      operated_by:       s.operated_by  || deal.airline || "",
    })),
    price:      deal.price != null ? String(deal.price) : "",
    currency:   deal.currency || "EUR",
    passengers: 1,
    baggage: {
      checked: {
        label:      "Checked Baggage",
        include:    raw.includes("check") || !!kgMatch,
        pieces:     1,
        weight:     kgMatch ? kgMatch[1] : "23",
        dimensions: "",
      },
      handCarry: {
        label:      "Cabin / Hand Carry",
        include:    raw.includes("carry") || raw.includes("cabin") || raw.includes("hand"),
        pieces:     1,
        weight:     "7",
        dimensions: "",
      },
      personalBag: {
        label:      "Personal Item",
        include:    false,
        pieces:     1,
        weight:     "",
        dimensions: "",
      },
    },
  };
}

// ─── Fetch image as base64 (needed to inline logo into SVG foreignObject) ─────

async function fetchAsBase64(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = e => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ─── Build deal HTML — identical structure to TicketGenerator.js buildHtml() ──
//
// This is the single source of truth for the visual output. The same HTML
// is used for:
//   1. The live iframe preview inside the modal
//   2. PNG generation via SVG foreignObject
//
// Colors and structure are taken verbatim from TicketGenerator.js.

function buildDealHtml(offer, logoSrc) {
  const logo = logoSrc || "https://thebetterfare.com/images/logo.png";

  // ── Flight segments (copied exactly from TicketGenerator.js segsHtml) ──
  const segsHtml = offer.segments.map((s, i) => `
    <tr><td style="padding:2px 0;">
      <table style="width:100%;border:1px solid #c7d2fe;border-radius:10px;overflow:hidden;" cellpadding="0" cellspacing="0">
        <tr><td style="background:#f0f4ff;padding:6px 12px;">
          <span style="font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:0.8px;">
            Flight ${i + 1} &nbsp;&middot;&nbsp; ${s.flight_number || "&mdash;"} &nbsp;&middot;&nbsp; ${s.operated_by || offer.airline || "&mdash;"} &nbsp;&middot;&nbsp; ${offer.cabin_class || "Economy"}
          </span>
        </td></tr>
        <tr><td style="background:#ffffff;padding:10px 14px;border-top:1px solid #c7d2fe;">
          <table style="width:100%;" cellpadding="0" cellspacing="0"><tr>
            <td style="text-align:center;width:38%;">
              <p style="margin:0;font-size:22px;font-weight:800;font-family:monospace;color:#1f2937;">${s.departure_time || "--:--"}</p>
              <p style="margin:2px 0 0;font-size:10px;color:#6b7280;">${displayDate(s.departure_date)}</p>
              <p style="margin:2px 0 0;font-size:14px;font-weight:700;color:#1e3a5f;font-family:monospace;">${s.departure_airport || "???"}</p>
              <p style="margin:1px 0 0;font-size:9px;color:#9ca3af;font-family:sans-serif;">${getAirportName(s.departure_airport) || ""}</p>
            </td>
            <td style="text-align:center;width:24%;padding:0 8px;">
              <div style="border-top:2px solid #93c5fd;position:relative;margin-top:12px;">
                <span style="position:absolute;top:-11px;left:50%;transform:translateX(-50%);background:white;padding:0 4px;font-size:15px;">&#9992;</span>
              </div>
            </td>
            <td style="text-align:center;width:38%;">
              <p style="margin:0;font-size:22px;font-weight:800;font-family:monospace;color:#1f2937;">${s.arrival_time || "--:--"}</p>
              <p style="margin:2px 0 0;font-size:10px;color:#6b7280;">${displayDate(s.arrival_date)}</p>
              <p style="margin:2px 0 0;font-size:14px;font-weight:700;color:#1e3a5f;font-family:monospace;">${s.arrival_airport || "???"}</p>
              <p style="margin:1px 0 0;font-size:9px;color:#9ca3af;font-family:sans-serif;">${getAirportName(s.arrival_airport) || ""}</p>
            </td>
          </tr></table>
        </td></tr>
      </table>
    </td></tr>`).join("");

  // ── Baggage table (same structure as ticket's passenger/baggage table) ──
  const bagItems = [offer.baggage.checked, offer.baggage.handCarry, offer.baggage.personalBag]
    .filter(b => b.include);

  const bagRowsHtml = bagItems.map((b, pi) => {
    const bg = pi % 2 === 0 ? "#f8fafc" : "#ffffff";
    const parts = [];
    if (b.pieces) parts.push(`${b.pieces} piece${b.pieces > 1 ? "s" : ""}`);
    if (b.weight) parts.push(`${b.weight}&nbsp;kg`);
    if (b.dimensions) parts.push(b.dimensions);
    return `<tr style="background:${bg};border-top:1px solid #e2e8f0;">
      <td style="padding:10px 16px;color:#1f2937;font-weight:600;font-size:13px;border-right:1px solid #e2e8f0;vertical-align:top;">&#10003;&nbsp;&nbsp;${b.label}</td>
      <td style="padding:8px 12px;color:#374151;font-size:12px;line-height:1.7;vertical-align:top;">${parts.join("&nbsp;&nbsp;&middot;&nbsp;&nbsp;") || "&mdash;"}</td>
    </tr>`;
  }).join("");

  const baggageSection = bagRowsHtml ? `
    <tr><td style="padding:10px 40px 0;">
      <table style="width:100%;table-layout:fixed;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;font-size:13px;" cellpadding="0" cellspacing="0">
        <colgroup><col style="width:45%"><col style="width:55%"></colgroup>
        <tr style="background:linear-gradient(90deg,#1e3a5f,#2d5986);">
          <td colspan="2" style="padding:8px 16px;font-size:10px;font-weight:800;color:white;text-transform:uppercase;letter-spacing:2px;">Baggage Allowance</td>
        </tr>
        ${bagRowsHtml}
      </table>
    </td></tr>` : "";

  // ── Price section (navy gradient — same as passenger table header) ──
  const priceNum     = parseFloat(offer.price) || 0;
  const priceDisplay = priceNum.toFixed(2);
  const paxNote      = offer.passengers > 1
    ? `${offer.passengers} passengers &nbsp;&middot;&nbsp; All taxes included`
    : "Per person &nbsp;&middot;&nbsp; All taxes included";

  // ── Full HTML — body/table structure identical to TicketGenerator.js ──
  return `<!DOCTYPE html><html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:8px;background:#f3f4f6;font-family:Helvetica,Arial,sans-serif;">
<table style="max-width:640px;margin:0 auto;background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(109,40,217,0.15);" cellpadding="0" cellspacing="0" width="100%">

  <tr><td style="background:#ffffff;padding:20px 40px 16px;text-align:center;border-bottom:3px solid #1e3a5f;">
    <img src="${logo}" alt="The Better Fare" style="height:48px;width:auto;display:block;margin:0 auto 12px;">
    <div style="display:inline-block;background:#f0f4ff;border:1px solid #bfcfff;border-radius:20px;padding:3px 14px;margin-bottom:8px;">
      <span style="font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;">Flight Offer</span>
    </div>
    <p style="margin:4px 0 0;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;">Subject to Availability</p>
  </td></tr>

  <tr><td style="padding:16px 40px 0;">
    <p style="font-size:13px;color:#6b7280;margin:0;line-height:1.7;">Please find your personalised flight offer below. Contact us to confirm and book.</p>
  </td></tr>

  <tr><td style="padding:12px 40px 0;">
    <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;margin:0 0 12px;border-left:3px solid #1e3a5f;padding-left:10px;">Flight Itinerary</p>
    <table style="width:100%;" cellpadding="0" cellspacing="0">${segsHtml}</table>
  </td></tr>

  ${baggageSection}

  <tr><td style="padding:10px 40px 0;">
    <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;" cellpadding="0" cellspacing="0">
      <tr style="background:linear-gradient(90deg,#1e3a5f,#2d5986);">
        <td style="padding:14px 18px;">
          <p style="margin:0;font-size:10px;font-weight:800;color:rgba(255,255,255,0.65);text-transform:uppercase;letter-spacing:2px;">Total Price</p>
          <p style="margin:4px 0 0;font-size:28px;font-weight:800;color:white;font-family:monospace;letter-spacing:1px;">${offer.currency}&nbsp;${priceDisplay}</p>
          <p style="margin:3px 0 0;font-size:11px;color:rgba(255,255,255,0.5);">${paxNote}</p>
        </td>
        <td style="padding:14px 18px;text-align:right;vertical-align:middle;">
          <p style="margin:0;font-size:14px;color:rgba(255,255,255,0.9);font-weight:700;">${offer.airline || ""}</p>
          <p style="margin:4px 0 0;font-size:12px;color:rgba(255,255,255,0.55);">${(offer.cabin_class || "Economy")}</p>
        </td>
      </tr>
    </table>
  </td></tr>

  <tr><td style="padding:14px 40px 12px;">
    <p style="font-size:13px;color:#374151;margin:0 0 4px;">To confirm this offer or for any questions, please reply to this message or contact us on WhatsApp.</p>
    <p style="font-size:11px;color:#9ca3af;margin:0 0 4px;">This offer is subject to availability and fares may change without notice.</p>
    <p style="font-size:11px;color:#9ca3af;margin:0;"><strong style="color:#6b7280;">Please note:</strong> This fare is not guaranteed until the booking has been confirmed by The Better Fare.</p>
  </td></tr>

  <tr><td style="background:linear-gradient(135deg,#1e1b4b,#312e81);padding:20px 40px;text-align:center;">
    <p style="margin:0 0 3px;color:rgba(255,255,255,0.95);font-size:13px;font-weight:700;letter-spacing:0.5px;">The Better Fare &nbsp;&middot;&nbsp; Atypical Agency</p>
    <p style="margin:0;color:rgba(255,255,255,0.4);font-size:11px;">www.thebetterfare.com &nbsp;&middot;&nbsp; KvK 88159620</p>
  </td></tr>

</table>
</body></html>`;
}

// ─── PNG via browser-side html2canvas ─────────────────────────────────────────
//
// html2canvas is loaded from cdnjs (allowed by CSP script-src) and renders an
// off-screen DOM element. The logo is already inlined as base64 so no CORS
// issues arise during canvas capture.

function loadHtml2Canvas() {
  return new Promise((resolve, reject) => {
    if (window.html2canvas) { resolve(window.html2canvas); return; }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    script.onload  = () => resolve(window.html2canvas);
    script.onerror = () => reject(new Error("Failed to load html2canvas"));
    document.head.appendChild(script);
  });
}

async function generatePng(htmlString, _unused, renderWidth = 680) {
  const h2c = await loadHtml2Canvas();

  // Render into a hidden container
  const container = document.createElement("div");
  container.style.cssText = `position:fixed;left:-9999px;top:0;width:${renderWidth}px;z-index:-1;`;
  container.innerHTML = htmlString;
  document.body.appendChild(container);

  try {
    const canvas = await h2c(container, {
      scale:            2,
      useCORS:          true,
      allowTaint:       false,
      backgroundColor:  "#f3f4f6",
      width:            renderWidth,
      windowWidth:      renderWidth,
      logging:          false,
    });
    return new Promise(resolve => canvas.toBlob(blob => resolve(URL.createObjectURL(blob)), "image/png"));
  } finally {
    document.body.removeChild(container);
  }
}

// ─── Validation ────────────────────────────────────────────────────────────────

export function validateCustomerOffer(offer) {
  const errors = [];
  if (!offer.airline.trim()) errors.push("Airline is required");
  const price = parseFloat(offer.price);
  if (!offer.price || isNaN(price) || price <= 0)
    errors.push("Price must be a positive number");
  offer.segments.forEach((s, i) => {
    const n = i + 1;
    if (!s.departure_airport.trim()) errors.push(`Flight ${n}: departure airport is required`);
    if (!s.arrival_airport.trim())   errors.push(`Flight ${n}: arrival airport is required`);
    if (!s.departure_date)           errors.push(`Flight ${n}: departure date is required`);
    if (!s.departure_time)           errors.push(`Flight ${n}: departure time is required`);
    if (!s.arrival_date)             errors.push(`Flight ${n}: arrival date is required`);
    if (!s.arrival_time)             errors.push(`Flight ${n}: arrival time is required`);
  });
  const bagItems = [offer.baggage.checked, offer.baggage.handCarry, offer.baggage.personalBag];
  for (const item of bagItems) {
    if (!item.include) continue;
    if (!item.pieces || item.pieces < 1) errors.push(`${item.label}: pieces must be at least 1`);
    if (item.pieces > 9)                 errors.push(`${item.label}: pieces cannot exceed 9`);
    if (item.weight !== "" && !/^\d+(\.\d+)?$/.test(item.weight))
      errors.push(`${item.label}: weight must be a number`);
  }
  return errors;
}

// Keep for backwards compatibility with existing tests
export function validateBaggageConfig(baggage) {
  return validateCustomerOffer({
    airline: "x", price: "1", passengers: 1,
    segments: [{ departure_airport: "A", arrival_airport: "B", departure_date: "2025-01-01", departure_time: "10:00", arrival_date: "2025-01-01", arrival_time: "12:00", flight_number: "", operated_by: "" }],
    baggage,
  }).filter(e => !e.startsWith("Flight") && e !== "Airline is required" && e !== "Price must be a positive number");
}

// ─── DealShareModal ────────────────────────────────────────────────────────────

export default function DealShareModal({ deal, open, onClose }) {
  const { getAuthHeaders }                = useAuth();
  const [offer,        setOffer]          = useState(() => initFromDeal(deal || {}));
  const [logoBase64,   setLogoBase64]     = useState(null);
  const [generating,   setGenerating]     = useState(false);
  const [imageDataUrl, setImageDataUrl]   = useState(null);
  const [errors,       setErrors]         = useState([]);
  const blobUrlRef                        = useRef(null);

  // Fetch logo as base64 once so it can be inlined into the HTML sent to the backend
  useEffect(() => {
    fetchAsBase64("https://thebetterfare.com/images/logo.png").then(setLogoBase64);
  }, []);

  // Revoke previous blob URL when a new image is generated or modal closes
  useEffect(() => {
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
  }, []);

  // Reset when deal changes
  useEffect(() => {
    if (deal) {
      setOffer(initFromDeal(deal));
      setImageDataUrl(null);
      setErrors([]);
    }
  }, [deal?.id]);

  // Live preview HTML — rebuilt whenever offer or logo changes
  const previewHtml = useMemo(
    () => buildDealHtml(offer, logoBase64),
    [offer, logoBase64]
  );

  // ── Updaters ──
  const setField = (key, val) => setOffer(o => ({ ...o, [key]: val }));

  const updateSeg = (i, field, val) =>
    setOffer(o => ({
      ...o,
      segments: o.segments.map((s, idx) => idx === i ? { ...s, [field]: val } : s),
    }));

  const addSeg = () =>
    setOffer(o => ({
      ...o,
      segments: [...o.segments, {
        departure_airport: "", arrival_airport: "",
        departure_date: "", departure_time: "",
        arrival_date:   "", arrival_time:   "",
        flight_number:  "", operated_by: o.airline,
      }],
    }));

  const removeSeg = i =>
    setOffer(o => ({ ...o, segments: o.segments.filter((_, idx) => idx !== i) }));

  const updateBag = (key, patch) =>
    setOffer(o => ({
      ...o,
      baggage: { ...o.baggage, [key]: { ...o.baggage[key], ...patch } },
    }));

  // ── Generate PNG ──
  const handleGenerate = useCallback(async () => {
    const errs = validateCustomerOffer(offer);
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setGenerating(true);
    try {
      const html     = buildDealHtml(offer, logoBase64);
      const blobUrl  = await generatePng(html);
      // Revoke previous blob URL before storing new one
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = blobUrl;
      setImageDataUrl(blobUrl);
    } catch (e) {
      console.error("PNG generation failed:", e);
      setErrors([e.message || "Image generation failed. Please try again."]);
    } finally {
      setGenerating(false);
    }
  }, [offer, logoBase64, getAuthHeaders]);

  const handleDownload = () => {
    if (!imageDataUrl) return;
    const a    = document.createElement("a");
    a.href     = imageDataUrl;
    const from = offer.segments[0]?.departure_airport || "deal";
    const to   = offer.segments[offer.segments.length - 1]?.arrival_airport || "";
    a.download = `flight-offer-${from}-${to}.png`.toLowerCase();
    a.click();
  };

  const handleWhatsApp = () => {
    const from  = offer.segments[0]?.departure_airport || "";
    const to    = offer.segments[offer.segments.length - 1]?.arrival_airport || "";
    const price = `${offer.currency} ${parseFloat(offer.price || 0).toFixed(2)}`;
    const text  = [
      `✈️ *Your Flight Offer from The Better Fare*`,
      ``,
      `Route: ${from} → ${to}`,
      `Airline: ${offer.airline}`,
      `Class: ${offer.cabin_class}`,
      `Price: ${price} (all taxes included)`,
      ``,
      `Please see the attached image for the full flight itinerary.`,
      ``,
      `To confirm this offer, please reply to this message.`,
      ``,
      `The Better Fare | hello@thebetterfare.com`,
    ].join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (!deal || !offer) return null;

  // ── Baggage keys ──
  const bagKeys = [
    { key: "checked",     label: "Checked Baggage" },
    { key: "handCarry",   label: "Cabin / Hand Carry" },
    { key: "personalBag", label: "Personal Item" },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* max-w-6xl two-column layout, 90vh tall */}
      <DialogContent
        className="max-w-6xl w-full p-0 gap-0 overflow-hidden flex flex-col"
        style={{ height: "90vh" }}
      >
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
            <ImageIcon className="w-5 h-5" />
            Share with Customer
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-0.5">
            Edit the customer-facing offer, then generate and download the PNG.
            The original deal is <strong>not modified</strong>.
          </p>
        </DialogHeader>

        {/* Body: edit left | preview+actions right */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* ── LEFT: Edit form ── */}
          <div className="w-[400px] shrink-0 border-r border-slate-200 overflow-y-auto p-5 space-y-6 bg-slate-50/40">

            {/* Flight details */}
            <section>
              <h3 className="text-[10px] font-bold text-[#1e3a5f] uppercase tracking-widest mb-3 border-l-2 border-[#1e3a5f] pl-2">
                Flight Details
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Airline</Label>
                  <Input
                    value={offer.airline}
                    onChange={e => setField("airline", e.target.value)}
                    className="mt-1 h-9"
                    placeholder="e.g. KLM"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Cabin Class</Label>
                  <Select value={offer.cabin_class} onValueChange={v => setField("cabin_class", v)}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Economy", "Premium Economy", "Business", "First"].map(c =>
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Segments */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[10px] font-bold text-[#1e3a5f] uppercase tracking-widest border-l-2 border-[#1e3a5f] pl-2">
                  Segments
                </h3>
                <Button size="sm" variant="outline" onClick={addSeg} className="h-7 text-xs border-[#c7d2fe] text-[#1e3a5f] hover:bg-[#f0f4ff]">
                  <Plus className="w-3 h-3 mr-1" />Add Leg
                </Button>
              </div>
              <div className="space-y-3">
                {offer.segments.map((s, i) => (
                  <div key={i} className="border border-[#c7d2fe] rounded-lg p-3 space-y-2 bg-white">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-[#1e3a5f] uppercase tracking-wide">Flight {i + 1}</span>
                      {offer.segments.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400 hover:text-red-600" onClick={() => removeSeg(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-slate-500">From (IATA)</Label>
                        <Input value={s.departure_airport} onChange={e => updateSeg(i, "departure_airport", e.target.value.toUpperCase())} className="mt-0.5 h-8 font-mono text-sm" placeholder="LHE" maxLength={3} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">To (IATA)</Label>
                        <Input value={s.arrival_airport} onChange={e => updateSeg(i, "arrival_airport", e.target.value.toUpperCase())} className="mt-0.5 h-8 font-mono text-sm" placeholder="AMS" maxLength={3} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-slate-500">Dep Date</Label>
                        <Input type="date" value={s.departure_date} onChange={e => updateSeg(i, "departure_date", e.target.value)} className="mt-0.5 h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Dep Time</Label>
                        <Input type="time" value={s.departure_time} onChange={e => updateSeg(i, "departure_time", e.target.value)} className="mt-0.5 h-8 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-slate-500">Arr Date</Label>
                        <Input type="date" value={s.arrival_date} onChange={e => updateSeg(i, "arrival_date", e.target.value)} className="mt-0.5 h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Arr Time</Label>
                        <Input type="time" value={s.arrival_time} onChange={e => updateSeg(i, "arrival_time", e.target.value)} className="mt-0.5 h-8 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-slate-500">Flight No.</Label>
                        <Input value={s.flight_number} onChange={e => updateSeg(i, "flight_number", e.target.value.toUpperCase())} className="mt-0.5 h-8 font-mono text-sm" placeholder="KL0157" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Operated By</Label>
                        <Input value={s.operated_by} onChange={e => updateSeg(i, "operated_by", e.target.value)} className="mt-0.5 h-8 text-sm" placeholder="KLM" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Price */}
            <section>
              <h3 className="text-[10px] font-bold text-[#1e3a5f] uppercase tracking-widest mb-3 border-l-2 border-[#1e3a5f] pl-2">
                Price
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Amount</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={offer.price}
                    onChange={e => setField("price", e.target.value)}
                    className="mt-1 h-9"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Currency</Label>
                  <Select value={offer.currency} onValueChange={v => setField("currency", v)}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["EUR","USD","GBP","AED","PKR","SAR","CHF"].map(c =>
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-xs text-slate-500">Number of Passengers</Label>
                <Input
                  type="number" min="1" max="20"
                  value={offer.passengers}
                  onChange={e => setField("passengers", parseInt(e.target.value) || 1)}
                  className="mt-1 h-9 w-24"
                />
              </div>
            </section>

            {/* Baggage */}
            <section>
              <h3 className="text-[10px] font-bold text-[#1e3a5f] uppercase tracking-widest mb-3 border-l-2 border-[#1e3a5f] pl-2">
                Baggage
              </h3>
              <div className="space-y-2">
                {bagKeys.map(({ key, label }) => {
                  const b = offer.baggage[key];
                  return (
                    <div key={key} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-white">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-800 text-sm">{label}</span>
                        <Switch
                          checked={b.include}
                          onCheckedChange={v => updateBag(key, { include: v })}
                        />
                      </div>
                      {b.include && (
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Pieces</Label>
                            <Input
                              type="number" min={1} max={9}
                              value={b.pieces}
                              onChange={e => updateBag(key, { pieces: parseInt(e.target.value) || 1 })}
                              className="h-8 text-sm mt-0.5"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Weight (kg)</Label>
                            <Input
                              type="text"
                              value={b.weight}
                              onChange={e => updateBag(key, { weight: e.target.value })}
                              className="h-8 text-sm mt-0.5"
                              placeholder="e.g. 23"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Dimensions</Label>
                            <Input
                              type="text"
                              value={b.dimensions}
                              onChange={e => updateBag(key, { dimensions: e.target.value })}
                              className="h-8 text-sm mt-0.5"
                              placeholder="optional"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

          </div>

          {/* ── RIGHT: Live preview + actions ── */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

            {/* Live preview iframe — exact same technique as TicketGenerator.js */}
            <div className="flex-1 min-h-0 overflow-hidden border-b border-slate-200 bg-[#f3f4f6]">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full border-none"
                title="Customer offer preview"
                sandbox="allow-same-origin"
              />
            </div>

            {/* Action area */}
            <div className="shrink-0 p-4 space-y-3 bg-white">

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-700 mb-1">Please fix:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {errors.map((e, i) => <li key={i} className="text-sm text-red-600">{e}</li>)}
                  </ul>
                </div>
              )}

              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d5986] text-white"
              >
                {generating ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Generating image…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />Generate PNG Image
                  </span>
                )}
              </Button>

              {imageDataUrl && (
                <>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleDownload}
                      variant="outline"
                      className="flex-1 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#f0f4ff]"
                    >
                      <Download className="w-4 h-4 mr-2" />Download PNG
                    </Button>
                    <Button
                      onClick={handleWhatsApp}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />Open WhatsApp
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 text-center">
                    Download the image first, then attach it when WhatsApp opens.
                  </p>
                </>
              )}

            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
